import { AlulaResponseData } from "@app/shared/databases/alula.database";
import { ImportMap } from "@app/shared/resources/class-importer-exporter.service";

export interface AlulaDeviceInt {
    id: string;
    csPhoneNumber: string;
    callerIdPhoneNumber: string;
    csAccountNumber: string;
    macAddress: string;
    crcCode: string;
    openCloseSuppression: string;
    csAuxNumber: string;
    dealerId: string;
    customerId: string;
    onlineStatus: boolean;
    relayGroupEnabled: boolean;
    receiverGroupId: string;
}

interface AlulaDeviceData {
    id: string;
    stationPhoneNumber: string;
    callerIdPhoneNumber: string;
    stationAccountNumber: string;
    mac: string;
    crc_code: string;
    openCloseSuppression: 0 | 1;
    stationAuxNumber: string;
    dealerId: string;
    customerId: string;
    onlineStatus: boolean;
    relayGroupEnabled: boolean;
    receiverGroupId: string;
}

export class AlulaDeviceHelper {
    static exportDeviceProperties(alulaDevice: AlulaDevice) {
        return {
            stationPhoneNumber: AlulaDevice.formatPhone(alulaDevice.csPhoneNumber) || null,
            callerIdPhoneNumber: AlulaDevice.formatPhone(alulaDevice.callerIdPhoneNumber) || null,
            stationAccountNumber: alulaDevice.csAccountNumber,
            stationAuxNumber: alulaDevice.csAuxNumber,
            openCloseSuppression: alulaDevice.openCloseSuppression,
            userSyncDisabled: true,
            overrideAccountNumber: true,
            // if the device exists or the device has a csPhoneNumber, use the current relayGroupEnabled property
            relayGroupEnabled: alulaDevice.id || alulaDevice.csPhoneNumber ? alulaDevice.relayGroupEnabled : true,
            receiverGroupId: alulaDevice.receiverGroupId,
        };
    }
}

export class AlulaDevice {
    static readonly importMap: ImportMap<AlulaDeviceInt, AlulaDeviceData> = [
        { i: "id", o: "id" },
        { i: "stationPhoneNumber", o: "csPhoneNumber" },
        { i: "callerIdPhoneNumber", o: "callerIdPhoneNumber" },
        { i: "stationAccountNumber", o: "csAccountNumber" },
        { i: "mac", o: "macAddress" },
        { i: "crc_code", o: "crcCode" },
        { i: "openCloseSuppression", o: "openCloseSuppression" },
        { i: "stationAuxNumber", o: "csAuxNumber" },
        { i: "dealerId", o: "dealerId" },
        { i: "customerId", o: "customerId" },
        { i: "onlineStatus", o: "onlineStatus" },
        { i: "relayGroupEnabled", o: "relayGroupEnabled" },
        { i: "receiverGroupId", o: "receiverGroupId" },
    ];

    static unformatPhone(phone: string) {
        let newPhone = ((phone + "").match(/\d/g) || []).join("");
        if (newPhone.length < 11 && newPhone) {
            newPhone = 1 + newPhone;
        }

        return newPhone;
    }

    static unformatPhoneNoCountryCode(phone: string) {
        let newPhone = ((phone + "").match(/\d/g) || []).join("");
        const index = newPhone.length - 10;
        newPhone = newPhone.substring(index);

        return newPhone;
    }

    static formatPhone(phone: string) {
        let newPhone = phone + "";

        if (phone.length === 11) {
            newPhone = phone.substring(1);
        } else if (phone.length === 12) {
            newPhone = phone.substring(2);
        }

        if (!phone) {
            return "";
        }

        return "(" + newPhone.substring(0, 3) + ")" + newPhone.substring(3, 6) + "-" + newPhone.substring(6, 10);
    }

    static formatPhoneWithCountry(phone: string) {
        if (phone) {
            let newPhone = phone + "";

            if (phone.length === 11) {
                newPhone = phone.substring(1);
            } else if (phone.length === 12) {
                newPhone = phone.substring(2);
            }

            return "1 (" + newPhone.substring(0, 3) + ") " + newPhone.substring(3, 6) + "-" + newPhone.substring(6, 10);
        }
        return "";
    }

    static fromDbObject(dbDevice: AlulaResponseData): AlulaDevice {
        return new AlulaDevice(
            dbDevice.id,
            AlulaDevice.unformatPhone(dbDevice.attributes[AlulaDevice.importMap[1].i]),
            AlulaDevice.unformatPhone(dbDevice.attributes[AlulaDevice.importMap[2].i]),
            dbDevice.attributes[AlulaDevice.importMap[3].i],
            dbDevice.attributes[AlulaDevice.importMap[4].i],
            dbDevice.attributes[AlulaDevice.importMap[5].i],
            dbDevice.attributes[AlulaDevice.importMap[6].i],
            dbDevice.attributes[AlulaDevice.importMap[7].i],
            null,
            dbDevice.attributes[AlulaDevice.importMap[8].i],
            dbDevice.attributes[AlulaDevice.importMap[9].i],
            dbDevice.attributes[AlulaDevice.importMap[10].i],
            dbDevice.attributes[AlulaDevice.importMap[11].i],
            dbDevice.attributes[AlulaDevice.importMap[12].i]
        );
    }

    isMonitoringServiceEnabled(): boolean {
        return !!this.customerId;
    }

    isPanelOnline(): boolean {
        return this.onlineStatus;
    }

    constructor(
        public id: string = "",
        public csPhoneNumber: string = "",
        public callerIdPhoneNumber: string = "",
        public csAccountNumber: string = "",
        public macAddress: string = "",
        public crcCode: string = "",
        public openCloseSuppression: boolean = false,
        public csAuxNumber: string = "",
        public overrideAccountNumber: boolean = true,
        public dealerId: string = "",
        public customerId: string = "",
        public onlineStatus: boolean = false,
        public relayGroupEnabled: boolean = false,
        public receiverGroupId: string = ""
    ) {}

    /**
     * @deprecated
     */
    exportDeviceProperties() {
        return {
            stationPhoneNumber: AlulaDevice.formatPhone(this.csPhoneNumber),
            callerIdPhoneNumber: AlulaDevice.formatPhone(this.callerIdPhoneNumber),
            stationAccountNumber: this.csAccountNumber,
            stationAuxNumber: this.csAuxNumber,
            openCloseSuppression: this.openCloseSuppression,
            userSyncDisabled: true,
            overrideAccountNumber: true,
            relayGroupEnabled: this.relayGroupEnabled || false,
            receiverGroupId: this.receiverGroupId,
        };
    }
}

/*
"relayGroupEnabled": false,
"interactiveEnabled": false,
"remoteUserLoggingFeatureEnabled": false,
"pushNotificationEnabled": true,
"userSyncDisabled": true,
"twoWayVoiceEnabled": false,
"siaTwoWayVoiceEnabled": false,
"mac": "54:21:60:10:49:35",
"sn": "084033096016073053",
"dealerId": "ef4d81d4-ba3a-ee8b-3201-594152eb04ff",
"deactiveCustomerId": "",
"customerId": "ef4d81d4-ba3a-ee8b-3201-594152eb04ff",
"dateActivated": "2017-09-26T15:52:36.000Z",
"autoCfg": 383,
"programId": 32,
"versionId": 0,
"meid": null,
"dateInserted": "2017-04-18 13:17:01",
"timezone": "America/Chicago",
"registeredBy": null,
"dateRegistered": "2017-06-15T00:49:26.000Z",
"onlineStatus": false,
"onlineStatusTimestamp": "2017-06-14T18:52:59.000Z",
"imei": null,
"iccid": null,
"mno": null,
"signalSuppressOpenClose": false,
"signalSuppressBypass": false,
"overrideAccountNumber": true,
"convertSiaToContactId": true,
"friendlyName": "084033096016073053",
"physicalAddress1": null,
"physicalAddress2": null,
"physicalStreetNumber": null,
"physicalStreetDirection": null,
"physicalCity": null,
"physicalState": null,
"physicalZip": null,
"physicalSiteNumber": null,
"physicalSuite": null,
"physicalFloor": null,
"physicalDescription": null,
"stationAccountNumber": "123456",
"stationAuxNumber": null,
"stationPhoneNumber": "(941)555-5555",
"callerIdPhoneNumber": "(941)444-4444",
"networkTroubleTimeout": 86400,
"openCloseSuppression": 1,
"receiverGroupId": null,
"downloaderPhoneNumber": "9794324224"
 */
